import React, { useEffect, useMemo, useState } from 'react';
import { Box, CheckBox, Text } from 'grommet';
import DroppableReportCanvas from './report-canvas';
import DraggableAvailableField from './available-field';
import { dropPositions } from './drag-utils';
import ReportRenderer from './report-renderer';
import {
  recencyFilters,
  getDatePeriodFromRecencyFilter,
  createContextEventUrl,
  canGroupHourly
} from '../reporting-utils';
import PeriodPicker from '../components/reports/period-picker';
import GroupBuilder from '../components/reports/group-builder';
import FilterSelector from '../components/reports/filter-selector';
import { METRICS, groups } from './metrics';
import { ReportFilterString } from './report-filter-string';
import {
  isApplicable,
  platforms
} from '../environment-config/environment-config';

export const GROUP_OPTIONS = [
  { label: 'SubAff', field: 'properties.url.queryParams.subaff' },
  { label: 'SubAff1', field: 'properties.url.queryParams.subaff1' },
  {
    label: 'Source ID (subaff2)',
    field: 'properties.url.queryParams.sourceid'
  },
  { label: 'SubAff3', field: 'properties.url.queryParams.subaff3' },
  { label: 'SubAff4', field: 'properties.url.queryParams.subaff4' },
  { label: 'SubAff5', field: 'properties.url.queryParams.subaff5' },
  { label: 'Date', field: 'timeStamp' },
  { label: 'Hour', field: 'timeStampHourly' },
  {
    label: 'Scope',
    field: 'scope.keyword'
  },
  {
    label: 'Advertiser',
    field: 'properties.advertiserId'
  },
  {
    label: 'Campaign',
    field: 'properties.campaignId'
  },
  {
    label: 'Campaign Type',
    field: 'properties.campaignType'
  },
  {
    label: 'Ad Group',
    field: 'properties.adGroupId'
  },
  {
    label: 'Ad Creative',
    field: 'properties.adCreativeId'
  },
  {
    label: 'Device Type',
    field: 'properties.device.osName'
  },
  {
    label: 'Targeted as Device',
    field: 'properties.device.inferredDeviceType.keyword'
  },
  {
    label: 'Campaign Position',
    field: 'properties.flowImpressionPosition'
  },
  {
    label: 'Campaign Conversion Type',
    field: 'properties.conversionTypeName.keyword'
  },
  {
    label: 'Sort Strategy',
    field: 'properties.sortStrategy.keyword'
  },
  {
    label: 'Ad Unit Display Type',
    field: 'properties.implementationType.keyword'
  },
  {
    label: 'Optimizely Experiment Id',
    field:
      'properties.integrations.optimizely.optimizelyExperiments.optimizelyExperimentId.keyword'
  },
  {
    label: 'Optimizely Variation Id',
    field:
      'properties.integrations.optimizely.optimizelyExperiments.optimizelyVariationId.keyword'
  },
  {
    label: 'Source Vertical',
    field: 'properties.sourceVerticalId.keyword'
  },
  {
    label: 'Source Subvertical',
    field: 'properties.sourceSubVerticalId.keyword'
  },
  {
    label: 'Campaign Vertical',
    field: 'properties.bannerData.campaignVerticalId.keyword'
  },
  {
    label: 'Campaign Subvertical',
    field: 'properties.bannerData.campaignSubVerticalId.keyword'
  },
  {
    label: 'Sort Version',
    field: 'properties.sortVersion.keyword'
  },
  {
    label: 'Sort Execution Info',
    field: 'properties.sortExecutionInfo.keyword'
  },
  {
    label: 'Business Unit',
    field: 'properties.businessUnitId.keyword'
  },
  {
    label: 'Ad Unit Render Type',
    field: 'properties.adUnitRenderType.keyword'
  },
  {
    label: 'Ip Resolved Country',
    field: 'properties.ipResolvedCountry.keyword'
  },
  {
    label: 'No Offer Reason',
    field: 'properties.noOfferReason.keyword'
  },
  {
    label: 'Traffic Partner',
    field: 'properties.trafficPartnerId.keyword'
  },
  {
    label: 'Sub Partner Id',
    field: 'properties.url.queryParams.subpartnerid'
  },
  {
    label: 'Sub Partner Name',
    field: 'properties.url.queryParams.subpartnername'
  },
  {
    label: 'Sub Partner Vertical Id',
    field: 'properties.url.queryParams.subpartnerverticalid'
  },
  {
    label: 'Sub Partner Subvertical Id',
    field: 'properties.url.queryParams.subpartnersubverticalid'
  },
  {
    label: 'Placement Experience',
    field: 'properties.url.queryParams.placementexperienceid'
  },
  {
    label: 'Placement Location',
    field: 'properties.url.queryParams.placementlocationid'
  },
  {
    label: 'Creative Title',
    field: 'properties.bannerData.offerTitle.keyword'
  },
  {
    label: 'Creative Description',
    field: 'properties.bannerData.offerDescription.keyword'
  },
  {
    label: 'Engagement Method',
    field: 'engagementMethod.keyword'
  },
  {
    label: 'Partner Configuration',
    field: 'properties.url.queryParams.configid'
  }
];

const MetricGroup = ({ groupMetrics, label }) =>
  groupMetrics.length > 0 && (
    <Box direction='column' flex={{ shrink: 0 }}>
      <Text textAlign='center' color='dark-4' size='small'>
        {label}
      </Text>
      {groupMetrics.map(gm => {
        return <DraggableAvailableField column={gm} key={gm.property} />;
      })}
    </Box>
  );

const ReportBuilder = ({ item, modifyItem, canEdit, addError }) => {
  //map string metrics to metric constant objects
  //keep this in state so we aren't generating a new array instance on every render pass
  const [metrics, setMetrics] = useState([]);
  useEffect(() => {
    //filter out unresolved metrics (e.g. legacy keys from older reports that
    //no longer map to a metric definition) so downstream consumers that expect
    //metric objects don't crash on undefined entries
    setMetrics(
      item.metrics
        .map(m => METRICS.find(am => am.property === m))
        .filter(Boolean)
    );
  }, [item.metrics]);

  const [isPreview, setIsPreview] = useState(false);
  const [recencyFilter, setRecencyFilter] = useState(
    recencyFilters.LAST_7_DAYS
  );
  const [customDatePeriod, setCustomDatePeriod] = useState(
    getDatePeriodFromRecencyFilter(recencyFilter)
  );
  const availableFields = METRICS.filter(
    f => !f.hidden && !item.metrics.find(m => m === f.property)
  );

  const partnerMetrics = availableFields.filter(
    af => af.group === groups.PARTNER
  );
  const campaignMetrics = availableFields.filter(
    af => af.group === groups.CAMPAIGN
  );

  const groupData = useMemo(() => {
    // array of options data, label|field
    let groupOptions = [...GROUP_OPTIONS];
    // array of strings containing the selected fields
    let additionalGroups = [...item.groups];

    if (
      !canGroupHourly(recencyFilter, customDatePeriod) ||
      item.groups.includes('timeStamp')
    ) {
      // remove hourly as a grouping option
      groupOptions = groupOptions.filter(
        groupOption => groupOption.field !== 'timeStampHourly'
      );
      const timeStampHourlyIdx = item.groups.indexOf('timeStampHourly');
      if (timeStampHourlyIdx !== -1) {
        // replace hourly with daily grouping
        additionalGroups.splice(timeStampHourlyIdx, 1, 'timeStamp');
        modifyItem({ groups: additionalGroups });
      }
    } else if (item.groups.includes('timeStampHourly')) {
      groupOptions = groupOptions.filter(
        groupOption => groupOption.field !== 'timeStamp'
      );
    }

    return {
      options: groupOptions,
      groups: additionalGroups
    };
  }, [item.groups, recencyFilter, customDatePeriod, modifyItem]);

  return (
    <Box direction='row' fill>
      <Box direction='column' fill>
        <Box border='bottom' pad='medium'>
          <Box direction='row' fill='horizontal' justify='between'>
            <Box direction='row' flex={{ shrink: 0 }} gap='medium'>
              <FilterSelector
                filters={item.filters ?? []}
                excludedFilters={item.excludedFilters ?? []}
                setFilters={newFilters => {
                  modifyItem({ filters: newFilters });
                }}
                setExcludedFilters={newExcludedFilters => {
                  modifyItem({ excludedFilters: newExcludedFilters });
                }}
                addError={addError}
              />
              <GroupBuilder
                availableGroups={groupData.options}
                selectedGroups={groupData.groups}
                setGroups={newGroups => {
                  modifyItem({ groups: newGroups });
                }}
                //  disabled={!isGroupAvailable}
              />
            </Box>

            <Box align='end'>
              <Box
                direction='row'
                align='center'
                gap='small'
                flex={{ shrink: 0 }}
              >
                {isApplicable({ platform: platforms.PXP }) && (
                  <CheckBox
                    label='Preview?'
                    reverse
                    toggle
                    checked={isPreview}
                    onChange={e => setIsPreview(e.target.checked)}
                  />
                )}
                <CheckBox
                  label='Trend?'
                  reverse
                  toggle
                  checked={item.showTrend === true ? true : false}
                  onChange={e => modifyItem({ showTrend: e.target.checked })}
                />
                <PeriodPicker
                  recencyFilter={recencyFilter}
                  setRecencyFilter={setRecencyFilter}
                  customDatePeriod={customDatePeriod}
                  setCustomDatePeriod={setCustomDatePeriod}
                  reportingMode
                />
              </Box>

              <Box>
                <Text style={{ fontStyle: 'italic', fontSize: '12px' }}>
                  Report data displayed in EST
                </Text>
              </Box>
            </Box>
          </Box>
          <ReportFilterString
            filters={item.filters}
            excludedFilters={item.excludedFilters}
          />
        </Box>
        <DroppableReportCanvas
          canEdit={canEdit}
          columns={metrics}
          onDrop={(column, targetColumn, dropPosition) => {
            const newMetrics = [...item.metrics];

            //moving an elemnt in the stack
            const existingColumnIndex = newMetrics.findIndex(
              m => m === column.property
            );
            if (existingColumnIndex !== -1) {
              newMetrics.splice(existingColumnIndex, 1);
            }

            if (targetColumn) {
              const targetColumnIndex = newMetrics.findIndex(
                m => m === targetColumn.property
              );
              if (targetColumnIndex !== -1) {
                newMetrics.splice(
                  dropPosition === dropPositions.AFTER
                    ? targetColumnIndex + 1
                    : targetColumnIndex,
                  0,
                  column.property
                );
              }
            } else {
              newMetrics.push(column.property);
            }

            modifyItem({ metrics: newMetrics });
          }}
          onRemoveColumn={column => {
            const newMetrics = [...item.metrics];
            newMetrics.splice(
              newMetrics.findIndex(m => m === column.property),
              1
            );

            modifyItem({ metrics: newMetrics });
          }}
        />
        <ReportRenderer
          reportDefinition={{
            groups: item.groups,
            fields: metrics,
            filters: item.filters,
            excludedFilters: item.excludedFilters,
            recencyFilter,
            customDatePeriod,
            queryEventUrl: createContextEventUrl({
              recency: recencyFilter,
              range: customDatePeriod
            }),
            showTrend: item.showTrend,
            isPreview: isPreview
          }}
          addError={addError}
        />
      </Box>

      <Box
        direction='column'
        fill='vertical'
        gap='small'
        pad='small'
        border='left'
        flex={{ shrink: 0 }}
        overflow={{ vertical: 'auto' }}
      >
        <Box flex={{ shrink: 0 }} border='bottom' pad={{ bottom: 'xsmall' }}>
          <Text textAlign='center' color='dark-4'>
            Metrics
          </Text>
        </Box>

        <MetricGroup groupMetrics={partnerMetrics} label='Partner' />
        <MetricGroup groupMetrics={campaignMetrics} label='Campaign' />
      </Box>
    </Box>
  );
};

ReportBuilder.displayName = 'ReportBuilder';

export default ReportBuilder;
